// src/components/admin/Category/ParentageTab.jsx
import React, { useState } from 'react';
import { useAllCategoryData } from '../../../hooks/UseCategoryData';
import { useToast } from '../../../hooks/use-toast'; // shadcn/ui useToast
import { Button } from '../../../components/ui/button';     // shadcn/ui Button
import { Input } from '../../../components/ui/input';       // shadcn/ui Input
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card'; // Import Card
import { PlusCircle, ArrowRightCircle, Download } from 'lucide-react'; // Added Download
import { useNavigate } from 'react-router-dom';
import api from '../../../services/apiServices';
import { CategorySearch } from './CategorySearch';



export default function ParentageTab({ category, locale }) {
  const { categories, loading, refresh } = useAllCategoryData();
  const [showChildForm, setShowChildForm] = useState(false); // Simplified to boolean
  const [showParentForm, setShowParentForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '' });
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  //const locale = useAdminStore((state) => state.locale); // <-- GET CONTEXT

  const isMasterLocale = locale === 'master';
  const [masterCategoryToImport, setMasterCategoryToImport] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const navigateToCategory = (categoryId, type) => {
    // --- MODIFIED: Include currentLocale in navigation ---
    if (!locale) {
        console.error("Cannot navigate: Current locale is unknown.");
        return; // Or navigate to a default/error page
    }
    const tab = type === 'child' ? 'info' : 'parentage'; // Default tab based on type
    navigate(`/admin/categories/manage/${locale}/view/${categoryId}/${tab}`);    // --- END MODIFIED ---
  };
  
  //Master create
  const handleCreate = async (type, parentId = null) => {
    if (!newCategory.name.trim() || !newCategory.slug.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Name and slug are required.",
      });
      return;
    }
    setIsCreating(true);
    const payload = {
      ...newCategory,
      name: newCategory.name.trim(),
      slug: newCategory.slug.trim(),
      type,
      parent_id: parentId,
    };

    try {
      const res = await api.post('/admin/categories/create', payload, {
        params: { locale },
      })
      await refresh(); // Refresh the category list
      setNewCategory({ name: '', slug: '' }); // Reset form
      if (type === 'child') setShowChildForm(false);
      if (type === 'parent') setShowParentForm(false);
      toast({
        variant: "success",
        title: "Success",
        description: `Category "${payload.name}" created successfully.`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Creation Failed",
        description: error.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleImport = async () => {
    if (!masterCategoryToImport || !locale || isMasterLocale) {
        toast({ variant: "destructive", title: "Import Error", description: "Please select a master category to import into the current locale." });
        return;
    }
    setIsImporting(true);
    try {
        const payload = { master_category_id: masterCategoryToImport.id };
        await api.post('/admin/categories/import-master', payload, { params: { locale: locale } }); // NEW Endpoint
        await refresh(); // Refresh the localized category list
        setMasterCategoryToImport(null); // Clear selection
        toast({ variant: "success", title: "Import Successful", description: `Master Category "${masterCategoryToImport.name}" imported into ${locale}.` });
    } catch (error) {
         toast({
            variant: "destructive",
            title: "Import Failed",
            description: error.response?.data?.detail || error.message || "Could not import the category.",
        });
    } finally {
        setIsImporting(false);
    }
  };

  const renderCreateForm = (type, parentId = null, onCancel) => (
    <div className="my-4 p-4 border rounded-md shadow-sm bg-card"> {/* Using card-like styling */}
      <h4 className="text-md font-semibold mb-3">
        Create New {type.charAt(0).toUpperCase() + type.slice(1)} Category
      </h4>
      <div className="space-y-3">
        <Input
          placeholder="Name"
          value={newCategory.name}
          onChange={e => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
          disabled={isCreating}
        />
        <Input
          placeholder="Slug"
          value={newCategory.slug}
          onChange={e => setNewCategory(prev => ({ ...prev, slug: e.target.value }))}
          disabled={isCreating}
        />
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline" // [cite: 11]
            onClick={onCancel}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleCreate(type, parentId)}
            disabled={isCreating}
            variant="default" // [cite: 10]
          >
            {isCreating ? `Creating ${type}...` : `Create ${type}`}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) return <p className="text-center p-4">Loading category tree...</p>;

  // Scenario: No specific category is selected (e.g., top-level category management view)
  if (isMasterLocale) {
  if (!category || !category.id) {
    return (
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">Manage Root Categories</h2>
        <Button onClick={() => setShowParentForm(prev => !prev)} variant="default" className="mb-4"> {/* [cite: 10] */}
          <PlusCircle className="mr-2 h-4 w-4" />
          {showParentForm ? 'Cancel Adding Parent' : 'Add New Master Parent Category'}
        </Button>
        {showParentForm && renderCreateForm('parent', null, () => setShowParentForm(false))}
        
        <h3 className="text-lg font-semibold mt-6 mb-2">Existing Parent Categories:</h3>
        {categories.filter(c => c.type === 'parent' && !c.parent_id).length > 0 ? (
           <ul className="space-y-1">
            {categories.filter(c => c.type === 'parent' && !c.parent_id).map(parentCat => (
              <li 
                key={parentCat.id}
                className="p-2 hover:bg-muted rounded-md cursor-pointer flex justify-between items-center"
                onClick={() => navigateToCategory(parentCat.id)}
              >
                <span>{parentCat.name} (ID: {parentCat.id})</span>
                <ArrowRightCircle className="h-4 w-4 text-muted-foreground" />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No parent categories found. Add one above!</p>
        )}
      </div>
    );
    }
  const currentCategoryIsParentType = category.type === 'parent';
  const displayParent = currentCategoryIsParentType ? category : categories.find(cat => cat.id === category.parent_id);
  const childrenOfDisplayParent = displayParent ? categories.filter(cat => cat.parent_id === displayParent.id) : [];
  
  return (
    <div className="p-4 space-y-6">
      <div>
        <h3 className="text-xl font-bold mb-1 flex items-center">
          {displayParent ? (
            <>
              <span 
                className="hover:underline cursor-pointer" 
                
                onClick={() => navigateToCategory(displayParent.id,displayParent.type)}
              >
                {displayParent.name}
              </span>
              <span className="text-sm text-muted-foreground ml-2">(ID: {displayParent.id}) - Parent</span>
            </>
          ) : (
             <span className="text-destructive">Selected category or its parent not found.</span>
          )}
        </h3>
        {displayParent && (
          <>
            {childrenOfDisplayParent.length > 0 ? (
              <ul className="ml-4 mt-2 space-y-1">
                {childrenOfDisplayParent.map(child => (
                  <li 
                    key={child.id} 
                    className={`p-2 rounded-md flex justify-between items-center group ${child.id === category.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'}`}
                  >
                    <span 
                      className="cursor-pointer group-hover:underline"
                      onClick={() => navigateToCategory(child.id,'child')}
                    >
                      {child.name}
                    </span>
                    <span className={`text-xs ${child.id === category.id ? 'text-accent-foreground/80' : 'text-muted-foreground'}`}>
                      (ID: {child.id}) - Child
                      {child.id === category.id && <span className="ml-2 font-semibold">(Currently Viewing)</span>}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="opacity-0 group-hover:opacity-100"
                      onClick={() => navigateToCategory(child.id,'child')}
                    >
                       View <ArrowRightCircle className="ml-2 h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ml-4 mt-2 text-sm text-muted-foreground">No child categories found for {displayParent.name}.</p>
            )}
            {/* Show 'Add Child' only if the *currently selected category* (from props) is a parent type */}
            {currentCategoryIsParentType && (
              <div className="mt-6">
                <Button onClick={() => setShowChildForm(prev => !prev)} variant="default"> {/* [cite: 10] */}
                  <PlusCircle className="mr-2 h-4 w-4" />
                  {showChildForm ? 'Cancel Adding Child' : `Add Child to ${category.name}`}
                </Button>
                {showChildForm && renderCreateForm('child', category.id, () => setShowChildForm(false))}
              </div>
            )}
          </>
        )}
      </div>
      {!displayParent && category.parent_id && (
         <p className="text-destructive text-sm">
            The parent category (ID: {category.parent_id}) for the currently selected child category was not found in the loaded categories. 
            The data might be inconsistent or the parent category is missing.
          </p>
      )}
    </div>
  );
  }
  else {
    // Scenario: No specific category selected (Localized view)
     if (!category || !category.id) {
       return (
         <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Manage Categories for Locale: {locale}</h2>
             {/* Import Section */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Import Master Category</CardTitle>
                    <CardDescription>Import a category and its structure from the Master list into the '{locale}' locale.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-sm font-medium">Select Master Category to Import:</p>
                    {/* CategorySearch needs context prop */}
                    <CategorySearch
                        locale="master" // Explicitly search master categories
                        onSelect={setMasterCategoryToImport}
                        //key="master-search-import" // Ensure it resets if needed
                    />
                    {masterCategoryToImport && (
                        <p className="text-sm text-muted-foreground">Selected: {masterCategoryToImport.name} (Type: {masterCategoryToImport.type})</p>
                    )}
                    <Button
                        onClick={handleImport}
                        disabled={!masterCategoryToImport || isImporting}
                    >
                        {isImporting ? 'Importing...' : <><Download className="mr-2 h-4 w-4" /> Import Selected Category</>}
                    </Button>
                </CardContent>
            </Card>

            <h3 className="text-lg font-semibold mt-6 mb-2">Existing Root Categories in {locale}:</h3>
            {/* List rendering uses localized categories */}
             {categories.filter(c => !c.parent_id).length > 0 ? (
               <ul className="space-y-1">
                 {categories.filter(c => !c.parent_id).map(rootCat => (
                   <li key={rootCat.id} onClick={() => navigateToCategory(rootCat.id, rootCat.type)} className="cursor-pointer hover:bg-muted p-2 rounded">
                    {rootCat.name} (ID: {rootCat.id}) - Type: {rootCat.type}
                   </li>
                 ))}
               </ul>
             ) : <p className="text-muted-foreground">No root categories found in this locale. Import one above.</p> }
         </div>
       );
     }

     // Scenario: Specific Localized category selected
     // Display parent/child hierarchy based on *localized* categories
     const currentCategoryIsParentType = category.type === 'parent';

  // 2. Find the parent to display (either the current category if it's a parent, or its actual parent if it's a child)
  //    Use the 'categories' list which is already filtered by the current locale via useAllCategoryData()
  const displayParent = currentCategoryIsParentType ? category : categories.find(cat => cat.id === category.parent_id);

  // 3. Find all children belonging to the displayParent
  const childrenOfDisplayParent = displayParent ? categories.filter(cat => cat.parent_id === displayParent.id) : [];

  return (
    <div className="p-4 space-y-6">
      <h2 className="text-lg font-semibold">Hierarchy {isMasterLocale ? '(Master)' : `in Locale: ${locale}`}</h2>
      <div>
        {/* Display Parent Info */}
        <h3 className="text-xl font-bold mb-1 flex items-center">
          {displayParent ? (
            <>
              <span
                className={`hover:underline cursor-pointer ${displayParent.id === category.id ? 'text-primary font-semibold' : ''}`} // Highlight if current is parent
                onClick={() => navigateToCategory(displayParent.id, displayParent.type)}
              >
                {displayParent.name}
              </span>
              <span className="text-sm text-muted-foreground ml-2">(ID: {displayParent.id}) - Parent</span>
            </>
          ) : (
            // Handle case where category has parent_id but parent isn't found (shouldn't happen often with correct data)
            category.parent_id ?
              <span className="text-destructive">Parent (ID: {category.parent_id}) not found in active list for '{locale}'.</span>
            : <span className="text-muted-foreground">Selected category is a root category.</span>
          )}
        </h3>

        {/* Display Children List (includes siblings and the current category if it's a child) */}
        {displayParent && (
          <>
            {childrenOfDisplayParent.length > 0 ? (
              <ul className="ml-4 mt-2 space-y-1">
                {childrenOfDisplayParent.map(child => (
                  <li
                    key={child.id}
                    className={`p-2 rounded-md flex justify-between items-center group ${
                      child.id === category.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    {/* Child Name (Clickable) */}
                    <span
                      className="cursor-pointer group-hover:underline"
                      onClick={() => navigateToCategory(child.id, child.type)}
                    >
                      {child.name}
                    </span>
                    {/* Child Info & Highlight */}
                    <span className={`text-xs ${
                      child.id === category.id ? 'text-accent-foreground/80' : 'text-muted-foreground'
                    }`}>
                      (ID: {child.id}) - {child.type === 'child' ? 'Child' : child.type} {/* Show type */}
                      {child.id === category.id && <span className="ml-2 font-semibold">(Currently Viewing)</span>}
                    </span>
                    {/* View Button (optional, shown on hover) */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100"
                      onClick={() => navigateToCategory(child.id, child.type)}
                    >
                       View <ArrowRightCircle className="ml-2 h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ml-4 mt-2 text-sm text-muted-foreground">No child categories found for {displayParent.name}.</p>
            )}

            {/* --- "Add Child" Button (Only in Master Locale AND if current category is Parent type) --- */}
            {isMasterLocale && currentCategoryIsParentType && (
              <div className="mt-6">
                <Button onClick={() => setShowChildForm(prev => !prev)} variant="default">
                  <PlusCircle className="mr-2 h-4 w-4" />
                  {showChildForm ? 'Cancel Adding Child' : `Add Master Child to ${category.name}`}
                </Button>
                {showChildForm && renderCreateForm('child', category.id, () => setShowChildForm(false))}
              </div>
            )}
          </>
        )}
      </div>

             {/* Import button can also be shown here if needed */}
             <Card className="mt-8">
                <CardHeader><CardTitle>Import Master Category</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                     <p className="text-sm font-medium">Select Master Category to Import:</p>
                    <CategorySearch locale="master" onSelect={setMasterCategoryToImport} key="master-search-import-2"/>
                    {masterCategoryToImport && <p className="text-sm text-muted-foreground">Selected: {masterCategoryToImport.name}</p>}
                    <Button onClick={handleImport} disabled={!masterCategoryToImport || isImporting}>
                        {isImporting ? 'Importing...' : <><Download className="mr-2 h-4 w-4" /> Import Selected Category into {locale}</>}
                    </Button>
                </CardContent>
             </Card>
        </div>
     );
  }

}